import { useCallback } from 'react';
import { useDeleteRelease, useUpdateRelease } from '@/features/releases/hooks/releases';
import { useTaggableTableRowActions } from '@/features/shared/hooks/useTaggableTableRowActions';
import type { TagColor } from '@/shared/data/tags';
import type { ReleaseListItemDto } from '@flowqa/shared';

type UseReleasesTableRowActionsParams = { selectedProjectId: string | null };

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
};

type UseReleasesTableRowActionsResult = {
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: ReleaseListItemDto) => void;
  onUpdateRowTagsColor: (input: {
    row: ReleaseListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useReleasesTableRowActions = ({
  selectedProjectId,
}: UseReleasesTableRowActionsParams): UseReleasesTableRowActionsResult => {
  const deleteReleaseMutation = useDeleteRelease();
  const updateReleaseMutation = useUpdateRelease();
  const updateRowTags = useCallback(
    async ({ row, tags }: { row: ReleaseListItemDto; tags: string[] }) => {
      await updateReleaseMutation.mutateAsync({ id: row.id, tags });
    },
    [updateReleaseMutation],
  );

  return useTaggableTableRowActions({
    deleteMutation: deleteReleaseMutation,
    entity: 'release',
    selectedProjectId,
    updateRowTags,
    updateRowTagsPending: updateReleaseMutation.isPending,
  });
};
